import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Globe, Smartphone, Wifi, Edit, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { type NetworkRule, useUpdateNetworkRule } from "@/hooks/useNetworkRules";
import { useDeleteNetworkRule } from "@/hooks/useDeleteNetworkRule";
import { UrlRulesSection } from "./UrlRulesSection";
import { RuleScopeConfiguration } from "./RuleScopeConfiguration";
import { SecondaryBundleIdSection } from "./SecondaryBundleIdSection";
import { AppIcon } from "./AppIcon";
import { useToast } from "@/hooks/use-toast";

interface NetworkRuleDetailProps {
  rule: NetworkRule;
  onBack: () => void;
  onDelete?: (ruleId: string) => void;
  ruleIdOverride?: string; // Explicit rule ID that takes precedence over rule.id
}

export const NetworkRuleDetail = ({ rule, onBack, onDelete, ruleIdOverride }: NetworkRuleDetailProps) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(rule.name);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(rule.description || "");
  const [searchParams, setSearchParams] = useSearchParams();

  const { toast } = useToast();
  const updateRuleMutation = useUpdateNetworkRule();
  const deleteRuleMutation = useDeleteNetworkRule();

  // Get current tab from URL, default to 'urls'
  const currentTab = searchParams.get('tab') || 'urls';

  // Priority: ruleIdOverride > rule.id > URL param
  const ruleIdFromUrl = searchParams.get('rule') || '';
  const effectiveRuleId = ruleIdOverride || ((rule as any)?.id as string | undefined) || ruleIdFromUrl;
  const ruleForChildren: NetworkRule = effectiveRuleId ? ({ ...rule, id: effectiveRuleId } as NetworkRule) : rule;

  // Handle tab changes and update URL
  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  // Determine rule URL type (webkit or socket)
  const ruleUrlType = rule.ruleType;
  const totalUrls = rule.urls.length;

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for the rule.",
        variant: "destructive",
      });
      return;
    }

    if (!effectiveRuleId) {
      toast({
        title: "Error",
        description: "Cannot update rule - rule ID is missing. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateRuleMutation.mutateAsync({
        ruleId: effectiveRuleId,
        updates: { name: editedName.trim() }
      });

      toast({
        title: "Rule Updated",
        description: "The rule name has been successfully updated.",
      });

      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating rule name:', error);
      toast({
        title: "Error",
        description: "Failed to update rule name. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveDescription = async () => {
    if (!effectiveRuleId) {
      toast({
        title: "Error",
        description: "Cannot update rule - rule ID is missing. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateRuleMutation.mutateAsync({
        ruleId: effectiveRuleId,
        updates: { description: editedDescription.trim() || null }
      });

      toast({
        title: "Description Updated",
        description: "The rule description has been successfully updated.",
      });

      setIsEditingDescription(false);
    } catch (error) {
      console.error('Error updating rule description:', error);
      toast({
        title: "Error",
        description: "Failed to update rule description. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancelNameEdit = () => {
    setEditedName(rule.name);
    setIsEditingName(false);
  };

  const handleCancelDescriptionEdit = () => {
    setEditedDescription(rule.description || "");
    setIsEditingDescription(false);
  };

  const handleAddApp = () => {
    // TODO: Implement app addition functionality
    toast({
      title: "Add App",
      description: "App addition functionality will be implemented.",
    });
  };

  const handleDeleteRule = async () => {
    if (!effectiveRuleId) {
      toast({
        title: "Error",
        description: "Cannot delete rule - rule ID is missing. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Only call the parent's onDelete callback - don't duplicate the deletion
      if (onDelete) {
        await onDelete(effectiveRuleId);
      } else {
        // Fallback: perform deletion here if no parent callback
        await deleteRuleMutation.mutateAsync(effectiveRuleId);

        toast({
          title: "Rule Deleted",
          description: "The network rule has been successfully deleted.",
        });

        onBack();
      }
    } catch (error: any) {
      console.error('Error deleting rule:', error);

      // Check if the error is about the rule being in use by levels
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('levels') && errorMessage.includes('using')) {
        toast({
          title: "Cannot delete rule",
          description: "This rule is currently being used by one or more levels. Please remove it from all levels before deleting.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete rule. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-6 md:px-8 lg:px-10">
      <div className="space-y-6">
        {/* Condensed Header Section */}
        <div className="space-y-4">
        {/* Back button and Delete button */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="flex items-center space-x-2 h-8 px-3 border"
          >
            <ArrowLeft className="w-3 h-3" />
            <span className="text-sm">Back</span>
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center space-x-2 h-8 px-3 border text-red-600 hover:text-red-700 hover:bg-red-50"
                disabled={deleteRuleMutation.isPending}
              >
                <Trash2 className="w-3 h-3" />
                <span className="text-sm">Delete</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the network rule "{rule.name}" and remove all associated URL rules and configurations.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleteRuleMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteRule}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={deleteRuleMutation.isPending}
                >
                  {deleteRuleMutation.isPending ? "Deleting..." : "Delete Rule"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Rule info */}
        <div className="space-y-3">
          {/* Rule name with edit functionality */}
          <div className="flex items-center space-x-2">
            {isEditingName ? (
              <div className="flex items-center space-x-2">
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="text-2xl font-bold h-auto py-2 px-3"
                  placeholder="Rule name"
                  disabled={updateRuleMutation.isPending}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      handleCancelNameEdit();
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={handleSaveName}
                  disabled={updateRuleMutation.isPending || !editedName.trim()}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelNameEdit}
                  disabled={updateRuleMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold text-gray-900">{rule.name}</h1>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditingName(true)}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Description with edit functionality */}
          <div className="flex items-start space-x-3">
            <div className="flex-1 max-w-md">
              <div className="text-sm font-medium text-gray-700 mb-1">Description</div>
              {isEditingDescription ? (
                <div className="space-y-2">
                  <Textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Add a description for this rule..."
                    disabled={updateRuleMutation.isPending}
                    autoFocus
                    className="min-h-[60px]"
                  />
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      onClick={handleSaveDescription}
                      disabled={updateRuleMutation.isPending}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelDescriptionEdit}
                      disabled={updateRuleMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start space-x-2">
                  {rule.description ? (
                    <p className="text-gray-600 text-sm leading-relaxed">{rule.description}</p>
                  ) : (
                    <p className="text-gray-400 text-sm italic">No description provided</p>
                  )}
                </div>
              )}
            </div>
            
            {/* Edit button and Last modified */}
            <div className="flex items-start space-x-3">
              {!isEditingDescription && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditingDescription(true)}
                  className="h-8 w-8 p-0 flex-shrink-0"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              )}
              
              <div className="text-right">
                <div className="text-xs text-gray-500">Last modified</div>
                <div className="text-sm text-gray-900 font-medium">{rule.lastModified}</div>
              </div>
            </div>
          </div>
          
          {/* App tags */}
          <div className="flex items-center space-x-2">
            {rule.type === "App Specific" && rule.apps && rule.apps.length > 0 ? (
              <>
                {rule.apps.map((app, index) => (
                  <Badge key={index} variant="outline" className="flex items-center space-x-2 px-3 py-1">
                    <AppIcon app_icon_url={app.iconUrl} app_name={app.appName} size="sm" />
                    <span>{app.appName}</span>
                  </Badge>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddApp}
                  className="h-7 w-7 p-0"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </>
            ) : rule.type === "App Specific" && rule.bundleId ? (
              <>
                <Badge variant="outline" className="flex items-center space-x-2 px-3 py-1">
                  <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                    <Smartphone className="w-3 h-3 text-gray-600" />
                  </div>
                  <span>{rule.appName || "Unknown App"}</span>
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddApp}
                  className="h-7 w-7 p-0"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </>
            ) : (
              <Badge variant="outline" className="flex items-center space-x-2 px-3 py-1">
                <Globe className="w-4 h-4 text-blue-600" />
                <span>System Wide</span>
              </Badge>
            )}
            
            {totalUrls > 0 && (
              <Badge variant="outline" className={ruleUrlType === 'webkit' ? 'text-blue-600 border-blue-200' : 'text-green-600 border-green-200'}>
                <div className="flex items-center space-x-1">
                  {ruleUrlType === 'webkit' ? (
                    <Globe className="w-3 h-3" />
                  ) : (
                    <Wifi className="w-3 h-3" />
                  )}
                  <span>{ruleUrlType === 'webkit' ? 'WebKit' : 'Socket'}</span>
                </div>
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Alternative Bundle ID Section for App-Specific Rules */}
      {rule.type === "App Specific" && (
        <SecondaryBundleIdSection rule={ruleForChildren} />
      )}

      {/* Tabs Section */}
      <Tabs value={currentTab} onValueChange={handleTabChange} className="space-y-6">
        <div className="border-b border-gray-200">
          <TabsList className="grid w-full grid-cols-2 bg-transparent h-auto p-0 rounded-none max-w-lg">
            <TabsTrigger 
              value="urls" 
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 rounded-none py-3 px-4 text-sm font-medium"
            >
              URL Rules ({totalUrls})
            </TabsTrigger>
            <TabsTrigger 
              value="scope-config"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 rounded-none py-3 px-4 text-sm font-medium"
            >
              Scope ({rule.scope.devices} devices)
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="urls" className="space-y-6">
          <UrlRulesSection rule={ruleForChildren} ruleId={effectiveRuleId} />
        </TabsContent>

        <TabsContent value="scope-config" className="space-y-6">
          <RuleScopeConfiguration rule={ruleForChildren} />
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};
